/**
 * Telling a mom her email is unconfirmed instead of telling her the password
 * she just typed correctly is wrong. Issue #1531.
 *
 * ── The failure this fixes ──────────────────────────────────────────────────
 *
 * A confirmation link that has expired or already been used drops her on
 * /signin (see src/pages/AuthCallback.tsx). She types the RIGHT password, and
 * SignIn.tsx rendered "Incorrect email or password" — because every non-network
 * auth error fell into one `else`. The next tap after reading that is "Forgot
 * password?", so the app sent her to reset a password that was never wrong.
 *
 * ⚠️ This is the same shape as the Aug 17 2026 incident in
 * `src/lib/authNetworkError.ts` — read that file's header. There, a dropped
 * connection was rendered as a credential failure; its fix pulled ONE cause out
 * of the catch-all `else`. This pulls out the second. The catch-all is the
 * hazard: any auth error we have not named reads as "your password is wrong".
 *
 * ── Verified, not assumed ───────────────────────────────────────────────────
 *
 * Against production auth on Sep 9 2026, signing in to an existing UNCONFIRMED
 * account with the correct password returns:
 *
 *   POST /auth/v1/token?grant_type=password  ->  400
 *   {"code":400,"error_code":"email_not_confirmed","msg":"Email not confirmed"}
 *
 * supabase-js surfaces GoTrue's `error_code` on `AuthError.code`
 * (`@supabase/auth-js` errors.d.ts — `code: ErrorCode | (string & {}) |
 * undefined`), and "email_not_confirmed" is a member of that typed union. So
 * the code check below is the primary signal, not a guess at wording.
 */

/**
 * What she reads. Deliberately says what is true and what to do, and nothing
 * about passwords.
 *
 * ⚠️ #1532 extends this into "…— check your inbox, or tap here for a fresh
 * link" once there is a resend to tap. Until then the sentence stands alone:
 * an honest dead end still beats a false explanation.
 */
export const EMAIL_NOT_CONFIRMED_MESSAGE =
  "Your email isn't confirmed yet — check your inbox for the confirmation link.";

/**
 * True only when GoTrue says the account exists and its email is unconfirmed.
 *
 * The `code` check is authoritative. The message fallback exists so that a
 * GoTrue wording or code change degrades to the HONEST copy rather than back to
 * "Incorrect email or password" — the whole point of this issue is that the
 * catch-all `else` is the dangerous branch, so anything recognisable should
 * leave it.
 */
export function isEmailNotConfirmedError(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const { code, message } = error as { code?: unknown; message?: unknown };

  if (code === "email_not_confirmed") return true;

  return typeof message === "string" && /email not confirmed/i.test(message);
}
